import { colors, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import Chart from "react-google-charts";

import { LoadingGuard } from "../../components/LoadingGuard";
import {
  useEnigmaPaths,
  useEnigmaTotalStartsCount,
} from "../../hooks/enigmaStats.hooks";
import { GamePathStatistics } from "../../libs/gameAnalysis/paths";
import { formatPercentage } from "../../libs/gameAnalysis/utils";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import { WorkerQueryTransferModes } from "../../libs/workers/consts";

export type EnigmaUsersPathsProps = {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
};

const COLORS_MAP: Record<string, string> = {
  Avvio: colors.blue[300],
  Completamento: colors.green[200],
  "Richiesta di Soluzione": colors.red[500],
  "Ispirazione 1": colors.yellow[200],
  "Ispirazione 2": colors.yellow[500],
  "Ispirazione 3": colors.yellow[800],
  "Ispirazione 4": colors.orange[500],
  "Ispirazione 5": colors.orange[700],
  "Ispirazione 6": colors.orange[900],
  Drop: colors.grey[900],
};

export function EnigmaUsersPaths({
  cityEscapeId,
  enigmaId,
  filters,
}: EnigmaUsersPathsProps) {
  const paths = useEnigmaPaths(cityEscapeId, enigmaId, filters, {
    response: { transferMode: WorkerQueryTransferModes.ARRAY_BUFFER },
  });
  const totalMatches = useEnigmaTotalStartsCount(
    cityEscapeId,
    enigmaId,
    filters
  );

  const formatTooltip = (path: GamePathStatistics) => {
    const segment = `${path.from} -> ${path.to}`;
    const gamePercentage = formatPercentage(
      path.count,
      totalMatches.data ?? 0,
      {
        fractionDigits: 1,
      }
    );
    const avgTime = formatSecondsToDDHHMMSS(path.time.avg);
    const avgErrors = path.errors.avg?.toFixed(2) ?? "0";
    return `${segment} | ${gamePercentage} delle partite |  ${avgTime} in media | ${avgErrors} errori in media`;
  };

  const sankeyRows = useMemo(() => {
    // console.log("Enigma data for paths:", paths.data);
    const header = ["From", "To", "Weight", { role: "tooltip" }];

    const rows = [];
    for (const path of paths.data ?? []) {
      if (isValidPath(path.from, path.to)) {
        rows.push([
          path.from,
          path.to,
          Number(path.count),
          formatTooltip(path),
        ]);
      }
    }

    return [header, ...rows];
  }, [paths]);

  const options = useMemo(() => {
    const colours = ensureColorSortingForCorrectAssignment(
      sankeyRows,
      COLORS_MAP
    );

    return {
      sankey: {
        iterations: 512,
        node: {
          nodePadding: 50,
          colors: colours,
        },
        link: { colorMode: "target" },
      },
    };
  }, [sankeyRows]);

  return (
    <Stack direction={"column"} spacing={2} width={"100%"}>
      <Typography variant="h6" gutterBottom>
        Segmentazione Percorsi di Gioco
      </Typography>
      <LoadingGuard isLoading={paths.isLoading || totalMatches.isLoading}>
        {(sankeyRows.length && (
          <Chart
            chartType="Sankey"
            width="100%"
            height="400px"
            data={sankeyRows}
            options={options}
          />
        )) || <>Nessun dato disponibile</>}
      </LoadingGuard>
    </Stack>
  );
}

/**
 * Ensures that the colors for the Sankey diagram are assigned correctly. Google Charts
 * requires the colors array to be in the same order as the nodes appear in the data.
 * @param sankeyData The data for the Sankey diagram
 * @param colorsMap A mapping of node names to colors
 * @returns An array of colors for the Sankey diagram nodes
 */
function ensureColorSortingForCorrectAssignment(
  sankeyData: any[],
  colorsMap: Record<string, string>
) {
  return sankeyData.reduce<string[]>((acc, row, index) => {
    if (index === 0) return acc; // Skip header
    const from = row[0] as string;
    const to = row[1] as string;
    if (!acc.includes(colorsMap[from])) {
      acc.push(colorsMap[from]);
    }
    if (!acc.includes(colorsMap[to])) {
      acc.push(colorsMap[to]);
    }
    return acc;
  }, []);
}

function isValidPath(from: string, to: string): boolean {
  if (from.startsWith("Ispirazione") && to.startsWith("Ispirazione")) {
    const lastFromChar = Number(from.slice(-1));
    const lastCharTo = Number(to.slice(-1));
    return lastFromChar < lastCharTo;
  }
  return true;
}
